import { useEffect } from "react";
import { MapPin, Mail } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, setSEO } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import { CONTACT_INFO } from "@/lib/data";

const ContactDetails = () => (
    <section className="py-28 md:py-36" data-testid="contact-section">
        <div className="sf-container grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
                <SectionHeading
                    overline="Get in Touch"
                    title="Let's Build the Future Together."
                    description="Share your drawings, specifications or production requirements — our business development team responds within one business day."
                />
                <Reveal delay={0.2}>
                    <div className="mt-14 space-y-0 border-t border-white/10">
                        {[
                            { icon: MapPin, label: "Address", value: CONTACT_INFO.address, testid: "contact-address" },
                            { icon: Mail, label: "Email", value: CONTACT_INFO.email, testid: "contact-email" },
                        ].map(({ icon: Icon, label, value, testid }) => (
                            <div key={label} className="flex items-start gap-5 py-6 border-b border-white/10 group" data-testid={testid}>
                                <Icon size={18} className="text-sf-green mt-1 shrink-0" />
                                <div>
                                    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">{label}</p>
                                    <p className="text-white/80 text-sm mt-2">{value}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="mt-10 inline-block border border-sf-green/40 text-sf-green font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2" data-testid="contact-iso-badge">
                        ISO 9001:2015 Certified
                    </p>
                </Reveal>
            </div>
            <div className="lg:col-span-7">
                <Reveal delay={0.25}>
                    <div className="border border-white/10 bg-sf-surface p-8 md:p-12" data-testid="contact-form-card">
                        <p className="sf-overline mb-8">Business Enquiry Form</p>
                        <EnquiryForm />
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);

const MapSection = () => (
    <section className="py-28 md:py-36 border-t border-white/10" data-testid="contact-map-section">
        <div className="sf-container">
            <SectionHeading
                overline="Find Us"
                title="Visit Our Facility"
                description="P.No. 9 & 20, Choudhary Rishal Singh Ind. Complex, Jeevan Nagar Part-1, Gounchi, Ballabgarh, Faridabad, India"
            />
            <Reveal delay={0.15}>
                <div className="sf-image-frame mt-14" data-testid="contact-map">
                    <iframe
                        title="Super Finishers — Ballabgarh, Faridabad"
                        src="https://maps.google.com/maps?q=Jeevan%20Nagar%2C%20Gounchi%2C%20Ballabgarh%2C%20Faridabad%2C%20Haryana%2C%20India&t=&z=15&ie=UTF8&iwloc=&output=embed"
                        className="w-full h-[420px] md:h-[520px] border-0 grayscale invert-[0.92] contrast-[0.9] opacity-90 hover:opacity-100 transition-opacity duration-500"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                    />
                </div>
            </Reveal>
        </div>
    </section>
);

export default function Contact() {
    useEffect(() => {
        setSEO("Contact Us — Super Finishers", "Discuss your mass manufacturing requirements with Super Finishers. Precision tubular components, automotive components and cycle parts — ISO 9001:2015 certified.");
    }, []);

    return (
        <>
            <PageHero
                overline="Contact Us"
                titleLines={["Start the", "Conversation."]}
                description="Mass manufacturing enquiries, custom component programs, facility visits — tell us what you need built."
                image="hero"
            />
            <ContactDetails />
            <MapSection />
        </>
    );
}
